import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, ExternalLink, Tag, Save, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ResponsiveLayout } from "@/components/layout/responsive-layout";
import { apiRequest } from "@/lib/queryClient";
import { TaskTemplateFormModal } from "@/components/modals/task-template-form-modal";
import type { TaskTemplate } from "@shared/schema";

interface EditingTemplate {
  id: number;
  taskName: string;
  taskDescription: string;
  targetDuration: string;
  formName: string;
  url: string;
  isActive: boolean;
  tags: string[];
}

export function TaskTemplates() {
  usePageTitle("Task Templates");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<EditingTemplate | null>(null);
  const [tagInput, setTagInput] = useState("");

  // Fetch task templates
  const { data: templates = [], isLoading } = useQuery<TaskTemplate[]>({
    queryKey: ['/api/task-templates']
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TaskTemplate> }) =>
      apiRequest('PUT', `/api/task-templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/task-templates'] });
      toast({
        title: "Success",
        description: "Task template updated successfully",
      });
      setEditingTemplate(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update task template",
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/task-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/task-templates'] });
      toast({
        title: "Success",
        description: "Task template deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete task template",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (template: TaskTemplate) => {
    setEditingTemplate({
      id: template.id,
      taskName: template.taskName,
      taskDescription: template.taskDescription || "",
      targetDuration: template.targetDuration || "",
      formName: template.formName || "",
      url: template.url || "",
      isActive: template.isActive ?? true,
      tags: template.tags || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingTemplate(null);
    setTagInput("");
  };

  const handleSaveEdit = () => {
    if (!editingTemplate) return;

    if (!editingTemplate.taskName.trim()) {
      toast({
        title: "Validation Error",
        description: "Task name is required",
        variant: "destructive",
      });
      return;
    }

    if (!editingTemplate.targetDuration.trim()) {
      toast({
        title: "Validation Error",
        description: "Target duration is required",
        variant: "destructive",
      });
      return;
    }

    updateMutation.mutate({
      id: editingTemplate.id,
      data: {
        taskName: editingTemplate.taskName,
        taskDescription: editingTemplate.taskDescription || undefined,
        targetDuration: editingTemplate.targetDuration,
        formName: editingTemplate.formName || undefined,
        url: editingTemplate.url || undefined,
        isActive: editingTemplate.isActive,
        tags: editingTemplate.tags.length > 0 ? editingTemplate.tags : undefined,
      },
    });
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this task template?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTemplate(null);
    setModalOpen(false);
  };

  const addTag = () => {
    if (!editingTemplate || !tagInput.trim()) return;
    
    const newTag = tagInput.trim();
    if (!editingTemplate.tags.includes(newTag)) {
      setEditingTemplate({
        ...editingTemplate,
        tags: [...editingTemplate.tags, newTag],
      });
    }
    setTagInput("");
  };

  const removeTag = (tagToRemove: string) => {
    if (!editingTemplate) return;
    
    setEditingTemplate({
      ...editingTemplate,
      tags: editingTemplate.tags.filter(tag => tag !== tagToRemove),
    });
  };

  const activeTemplates = templates.filter(t => t.isActive);
  const inactiveTemplates = templates.filter(t => !t.isActive);

  const renderTemplate = (template: TaskTemplate, isInactive = false) => {
    const isEditing = editingTemplate?.id === template.id;

    if (isEditing) {
      return (
        <Card key={template.id} className="border-primary" data-testid={`card-template-${template.id}`}>
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Editing Task Template</h4>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={updateMutation.isPending}
                    data-testid={`button-cancel-edit-${template.id}`}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                    data-testid={`button-save-edit-${template.id}`}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Task Name *</label>
                    <Input
                      value={editingTemplate.taskName}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, taskName: e.target.value })}
                      placeholder="Enter task name"
                      data-testid={`input-task-name-${template.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Target Duration *</label>
                    <Input
                      value={editingTemplate.targetDuration}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, targetDuration: e.target.value })}
                      placeholder="e.g., 2-3 days"
                      data-testid={`input-target-duration-${template.id}`}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Description</label>
                  <Textarea
                    value={editingTemplate.taskDescription}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, taskDescription: e.target.value })}
                    placeholder="Enter task description"
                    rows={3}
                    data-testid={`input-task-description-${template.id}`}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Form Name</label>
                    <Input
                      value={editingTemplate.formName}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, formName: e.target.value })}
                      placeholder="e.g., BOC-3"
                      data-testid={`input-form-name-${template.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">URL</label>
                    <Input
                      value={editingTemplate.url}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, url: e.target.value })}
                      placeholder="https://example.com"
                      data-testid={`input-url-${template.id}`}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Tags</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Add a tag and press Enter"
                      data-testid={`input-tag-${template.id}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addTag}
                      data-testid={`button-add-tag-${template.id}`}
                    >
                      Add
                    </Button>
                  </div>
                  {editingTemplate.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {editingTemplate.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tag}
                          <button
                            onClick={() => removeTag(tag)}
                            className="ml-1 hover:text-destructive"
                            data-testid={`button-remove-tag-${tag}-${template.id}`}
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`active-${template.id}`}
                    checked={editingTemplate.isActive}
                    onCheckedChange={(checked) => 
                      setEditingTemplate({ ...editingTemplate, isActive: !!checked })
                    }
                    data-testid={`checkbox-active-${template.id}`}
                  />
                  <label htmlFor={`active-${template.id}`} className="text-sm font-medium cursor-pointer">
                    Active
                  </label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card 
        key={template.id} 
        className={`hover:shadow-md transition-shadow ${isInactive ? 'opacity-60' : ''}`}
        data-testid={`card-template-${template.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-medium" data-testid={`text-template-name-${template.id}`}>
                  {template.taskName}
                </h4>
                <Badge variant="secondary" className="text-xs">
                  {template.targetDuration}
                </Badge>
                {isInactive && <Badge variant="outline" className="text-xs">Inactive</Badge>}
              </div>
              {template.taskDescription && (
                <p className="text-sm text-muted-foreground mb-2" data-testid={`text-template-description-${template.id}`}>
                  {template.taskDescription}
                </p>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                {template.formName && (
                  <Badge variant="outline" className="text-xs" data-testid={`badge-form-${template.id}`}>
                    Form: {template.formName}
                  </Badge>
                )}
                {template.url && (
                  <a 
                    href={template.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    data-testid={`link-template-url-${template.id}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {template.url.length > 40 ? template.url.substring(0, 40) + '...' : template.url}
                  </a>
                )}
                {template.tags && template.tags.length > 0 && (
                  <div className="flex items-center gap-1" data-testid={`tags-${template.id}`}>
                    <Tag className="h-3 w-3 text-muted-foreground" />
                    {template.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleEdit(template)}
                data-testid={`button-edit-${template.id}`}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleDelete(template.id)}
                data-testid={`button-delete-${template.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <ResponsiveLayout 
      title="Task Templates" 
      subtitle="Manage reusable task templates that can be composed into service templates"
      currentPage="task-templates"
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-muted-foreground">
              {activeTemplates.length} active template{activeTemplates.length !== 1 ? 's' : ''}
              {inactiveTemplates.length > 0 && ` • ${inactiveTemplates.length} inactive`}
            </p>
          </div>
          <Button onClick={handleCreate} data-testid="button-create-task-template">
            <Plus className="mr-2 h-4 w-4" />
            Add Task Template
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Loading task templates...</div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">No task templates found.</p>
              <Button onClick={handleCreate} className="mt-4" data-testid="button-create-first-template">
                <Plus className="mr-2 h-4 w-4" />
                Create First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {activeTemplates.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">Active Templates</h3>
                <div className="grid gap-3">
                  {activeTemplates.map((template) => renderTemplate(template))}
                </div>
              </div>
            )}

            {inactiveTemplates.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">Inactive Templates</h3>
                <div className="grid gap-3">
                  {inactiveTemplates.map((template) => renderTemplate(template, true))}
                </div>
              </div>
            )}
          </div>
        )}

        <TaskTemplateFormModal
          open={modalOpen}
          onClose={handleCloseModal}
          template={selectedTemplate}
        />
      </div>
    </ResponsiveLayout>
  );
}
